/**
 * ===================================================================================
 * 📁 경로: /services/frontend/src/components/MessageModal.js
 * 📌 프로그램명: 메시지 알림 모달 컴포넌트
 * 🧩 개요:
 * - 사용자에게 정보(Info), 경고(Warning), 오류(Error) 메시지를 모달 형태로 제공합니다.
 * - 기존 DuplicateFoundModal과 스타일을 공유하여 일관된 UX를 제공합니다.
 * ===================================================================================
 */
import React from 'react';

/**
 * [Why]
 * 시스템에서 발생하는 다양한 알림을 사용자에게 명확하게 전달하기 위해 사용됩니다.
 * 특히 에러 상황에서 사용자의 주의를 환기시키기 위해 모달 형태를 채택했습니다.
 *
 * [How]
 * - `type` prop에 따라 아이콘과 색상을 다르게 표시합니다.
 * - `isOpen`이 true일 때만 렌더링됩니다.
 *
 * @component
 * @param {object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {Function} props.onClose - 닫기 버튼 핸들러
 * @param {string} props.title - 모달 제목
 * @param {string} props.message - 표시할 메시지
 * @param {'info'|'warning'|'error'} [props.type='info'] - 메시지 유형 (기본값: info)
 */
function MessageModal({ isOpen, onClose, title, message, type = 'info' }) {
    if (!isOpen) return null;

    // 타입별 아이콘 및 색상 설정
    const getIcon = () => {
        switch (type) {
            case 'error':
                return (
                    <div className="message-modal-icon error">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="message-modal-icon warning">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </div>
                );
            default: // info
                return (
                    <div className="message-modal-icon info">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </div>
                );
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {getIcon()}
                <h3 style={{ marginTop: '16px' }}>{title}</h3>
                <p style={{ whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>{message}</p>
                <div className="modal-actions">
                    <button className="btn btn-primary" onClick={onClose}>
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MessageModal;
